import { useCallback, useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { Box, Button, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { debounce } from '@mui/material/utils';
import useSalesforceReports from 'hooks/useSalesforceReports';
import ReportList from '../components/AlertCenter/ReportList';
import { useIRWorkstation } from 'features/IRWorkstation/contexts/IRWorkstationContext';

// Track Events
import { useTrackEvent } from 'hooks/useTrackEvent';
import { ButtonClickTargets } from 'constants/buttonClickTargets';
import { PageLoadsTargets } from 'constants/pageLoadTargets';

const SelectReportForAlert = () => {
	const { tenant } = useIRWorkstation();
	const navigate = useNavigate();
	const { trackButtonClick, trackPageLoad } = useTrackEvent({ tenant, product: 'IEM' });

	const { reports, loading, error, totalCount } = useSalesforceReports();
	const [searchQuery, setSearchQuery] = useState('');

	const handleButtonClickTrackEvent = (targetKey) => {
		const targetMap = {
			search: ButtonClickTargets.AlertCenterReportSearch,
			select: ButtonClickTargets.AlertCenterReportSelect,
			backToAlerts: ButtonClickTargets.AlertCenterReportBackToAlerts,
		};

		const target = targetMap[targetKey];
		if (target) {
			trackButtonClick(target);
		}
	};

	// Debounce the search tracking with a longer delay (1000ms) to avoid firing on every keystroke
	const debouncedSearchTracking = useMemo(
		() => debounce(() => handleButtonClickTrackEvent('search'), 1000),
		[]
	);

	// Clear debounce on unmount
	useEffect(
		() => () => debouncedSearchTracking.clear(),
		[debouncedSearchTracking]
	);

	const filteredReports = useMemo(() => {
		if (!searchQuery) return reports;
		const query = searchQuery.trim().toLowerCase();

		debouncedSearchTracking(); // Only fires after 1000ms of no typing
		return reports.filter(
			(report) =>
				report.name?.toLowerCase().includes(query) ||
				report.folder_name?.toLowerCase().includes(query)
		);
	}, [reports, searchQuery, debouncedSearchTracking]);

	// Take user to report analysis page after report selection
	const handleSelectedReport = useCallback(
		(report) => {
			navigate(`analysis/${report.id}`, {
				state: { reportName: report.name },
			});
		},
		[navigate]
	);

	useEffect(() => {
		trackPageLoad(PageLoadsTargets.NewAlertReport);
	}, []);

	return (
		<Box
			sx={{
				flex: 1,
				minWidth: 0,
				overflowY: 'auto',
				backgroundColor: 'background.default',
				padding: '12px 20px',
				display: 'flex',
				flexDirection: 'column',
				gap: '14px',
			}}
		>
			<Box>
				<Button
					startIcon={<ArrowBackIcon />}
					onClick={() => {
						handleButtonClickTrackEvent('backToAlerts');
						navigate('..');
					}} // Return to parent route
				>
					Alerts
				</Button>
			</Box>

			{/* Header */}
			<Box>
				<Typography sx={{ fontSize: '17px', fontWeight: 700 }}>
					New Alert - Pick a Report
				</Typography>
				<Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
					Choose the report to watch
				</Typography>
			</Box>

			{/* Report list Container*/}
			<Box
				sx={{
					flex: 1,
					minHeight: 0,
				}}
			>
				{error ? (
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							height: '100%',
							border: '0.5px solid',
							borderColor: 'border.light',
							borderRadius: '8px',
						}}
					>
						<Typography
							sx={{
								fontSize: '12px',
								color: 'text.secondary',
								textAlign: 'center',
								px: 2,
							}}
						>
							{error === 'not_connected'
								? 'Connect your CRM to view reports here.'
								: error === 'reconnect_required'
									? "Connection expired. Please remove and reconnect your CRM from Launchpad's Connections page."
									: 'Unable to load reports. Please try again later.'}
						</Typography>
					</Box>
				) : (
					<ReportList
						reports={filteredReports}
						loading={loading}
						searchQuery={searchQuery}
						setSearchQuery={setSearchQuery}
						handleSelectedReport={(report) => {
							handleButtonClickTrackEvent('select');
							handleSelectedReport(report);
						}}
						totalCount={totalCount}
					/>
				)}
			</Box>
		</Box>
	);
};

export default SelectReportForAlert;
